import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  ColorDisabled,
  IgButton,
  IgIconButton,
  IgTypography,
} from "@intelligems/ig-theme";
import { Close } from "@mui/icons-material";
import {
  Card,
  CardContent,
  CardMedia,
  Dialog,
  Divider,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { useGetExperiencesList } from "../../api/experiences-list/get-experiences-list";
import { useGetOrg } from "../../api/organizations/get-organization";
import { useLoggedInUser } from "../../api/user-auths/login";
import { usePatchUser } from "../../api/users/patch-user";
import { OfferIcon } from "../../images/Icons/Offer/OfferIcon";
import { PlayPauseIcon } from "../../images/Icons/Offer/PlayPauseIcon";
import { RolloutIcon } from "../../images/Icons/Offer/RolloutIcon";
import { TargetVisitorsIcon } from "../../images/Icons/Offer/TargetVisitorsIcon";
import PersonalizationsDialogImg from "../../images/PersonalizationsDialogImg.png";
import { PERSONALIZATION_ROUTES } from "../../routes/routes";
import {
  setViewedCampaignDialog,
  setViewedInfoPopup,
} from "../../store/slices/appSettings";
import { isDialogOpen, setDialog } from "../../store/slices/global";
import { contactSales } from "../../utils/contactSupport";

const PREFIX = "UserInfoPopup";

const classes = {
  root: `${PREFIX}-root`,
  dialogTitle: `${PREFIX}-dialogTitle`,
  dialogText1: `${PREFIX}-dialogText1`,
  dialogText2: `${PREFIX}-dialogText2`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  // marginLeft: "190px",
  [`& .${classes.dialogTitle}`]: {
    fontSize: "20px",
    fontWeight: 600,
    lineHeight: "unset",
  },
  [`& .${classes.dialogText1}`]: {
    marginTop: "15px",
    marginBottom: "15px",
    color: theme.palette.grey[900],
  },
  [`& .${classes.dialogText2}`]: {
    marginBottom: 15,
  },
}));
const informationCards = [
  {
    title: "Modify Your Shop",
    icon: <OfferIcon />,
    description:
      "Offer discounts and deals, overhaul your branding, fine tune your text and layouts, or combine many changes to create a compelling experience.",
  },
  {
    title: "Target Specific Visitors",
    icon: <TargetVisitorsIcon />,
    description:
      "Apply changes to all site visitors, or limit them to specific audiences like new visitors, those from a social campaign, a specific country, or a special link.",
  },
  {
    title: "Start & Stop Anytime",
    icon: <PlayPauseIcon />,
    description:
      "Leave a Personalization active year round to optimize your site for a specific audience, run it briefly for an event, or turn it on annually for seasonal promotions.",
  },
  {
    title: "Roll Out Test Groups",
    icon: <RolloutIcon />,
    description:
      "Roll out an experiment’s winning group into a live Personalization in a few clicks, optimizing your site without any Shopify or development work.",
  },
];

// 7/22/24 - CP - currently being used for personalizations dialog
export const UserInfoPopup = () => {
  const showUserInfoPopup = useSelector(isDialogOpen("showUserInfoPopup"));
  const existingCampaigns = useGetExperiencesList({
    category: "personalization",
  });
  const dispatch = useDispatch();
  const org = useGetOrg();
  const navigate = useNavigate();
  const patchUser = usePatchUser();
  const user = useLoggedInUser();

  const handleClose = (navigateTo?: boolean) => {
    if (user.isSuccess && user.data.user.id) {
      patchUser.mutate({
        queryParams: undefined,
        pathParams: { id: user.data.user.id },
        body: {
          user: {
            organizationId: org.data.org.id,
            userAlerts: {
              ...user.data.user.userAlerts,
              showPersonalizationsDialog: false,
            },
            id: user.data.user.id,
          },
        },
      });
      dispatch(setViewedCampaignDialog());
      dispatch(setDialog({ dialog: "showUserInfoPopup", open: false }));
      navigateTo && navigate(PERSONALIZATION_ROUTES.Personalizations);
    }
  };

  const hasExistingCampaigns = useMemo(() => {
    if (existingCampaigns.isSuccess) {
      return existingCampaigns.data.experiencesList.length > 0;
    } else return false;
  }, [existingCampaigns.isSuccess]);

  return (
    <StyledDialog
      maxWidth={"md"}
      open={showUserInfoPopup}
      aria-labelledby={"user-info-popup"}
      aria-describedby={"user-info-popup"}
      onClose={() => {
        handleClose();
      }}
    >
      <Card elevation={0} sx={{ textAlign: "center" }}>
        <CardMedia
          sx={{ height: "320px" }}
          image={PersonalizationsDialogImg}
          title="index"
        >
          <Grid
            container
            justifyContent={"center"}
            direction={"column"}
            alignItems={"center"}
            padding={1}
          >
            <Grid item container direction={"row"} justifyContent={"flex-end"}>
              <IgIconButton
                onClick={() => {
                  handleClose();
                }}
              >
                <Close sx={{ color: ColorDisabled.main }} />
              </IgIconButton>
            </Grid>
            <Grid item mt={1}>
              <IgTypography sx={{ fontWeight: 600, fontSize: "18px" }}>
                {hasExistingCampaigns
                  ? "Your Campaigns have been upgraded to"
                  : "Introducing"}
              </IgTypography>
            </Grid>
            <Grid item mb={2}>
              <IgTypography sx={{ fontWeight: 600, fontSize: "24px" }}>
                PERSONALIZATIONS
              </IgTypography>
            </Grid>
            <Grid
              item
              mb={3}
              container
              direction={"row"}
              justifyContent={"center"}
              sx={{ width: "450px" }}
            >
              <IgTypography sx={{ fontSize: "18px" }}>
                {hasExistingCampaigns
                  ? "Go beyond offers. Maximize revenue by custom\n" +
                    "tailoring every visitor’s shopping experience with targeted layouts, branding and copy."
                  : "Maximize revenue by custom tailoring every visitor’s shopping experience with targeted offers, layouts, branding and copy."}
              </IgTypography>
            </Grid>
            <Grid item>
              <IgButton
                onClick={() => {
                  handleClose(true);
                }}
                variant={"contained"}
              >
                {hasExistingCampaigns
                  ? "View Your Personalizations"
                  : "Try Personalizations"}
              </IgButton>
            </Grid>
            <Grid item>
              <IgButton
                onClick={() => {
                  contactSales();
                  handleClose(true);
                }}
                variant={"text"}
              >
                Contact Us
              </IgButton>
            </Grid>
          </Grid>
        </CardMedia>
        <CardContent>
          <Grid
            item
            container
            direction={"row"}
            justifyContent={"center"}
            columnSpacing={1}
            rowSpacing={1}
            ml={1.5}
          >
            {informationCards.map((card, index) => (
              <Grid
                item
                container
                direction={"row"}
                xs={3}
                alignItems={"center"}
              >
                <Grid
                  xs={10}
                  item
                  container
                  direction={"column"}
                  justifyContent={"center"}
                  rowSpacing={2}
                >
                  <Grid item>{card.icon}</Grid>
                  <Grid item>
                    <IgTypography
                      sx={{
                        fontSize: "15px",
                      }}
                      igStyle={"semibold"}
                    >
                      {card.title}
                    </IgTypography>
                  </Grid>
                  <Grid item>
                    <IgTypography
                      sx={{
                        // maxWidth: "175px",
                        color: "#6C6C6C",
                        fontSize: "14px",
                      }}
                    >
                      {card.description}
                    </IgTypography>
                  </Grid>
                </Grid>
                {informationCards.length - 1 > index && (
                  <Grid item xs={1.2}>
                    <Divider
                      orientation={"vertical"}
                      sx={{ height: "135px" }}
                    />
                  </Grid>
                )}
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </StyledDialog>
  );
};
